import { quoteActivityFormDefault } from "@factory/form/form.default";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

// import { quoteActivityFormDefault } from "../../../factory/form/form.default";
import { QuotationFormSchema } from "../../../factory/form/form.rule";
import { GetActivityModel } from "../../../factory/model/query-model-activity";
import { useQyGetActivity } from "../../../factory/query/activity.query";
import { useQyGetRatePerPax } from "../../../factory/query/rate-per-pax.query";
import { LabelValue } from "../../../shared/models/label-value.interface";
import DropdownControl from "../../../shared/ui/hook-form/dropdown-control/dropdown-control";
import InputDigitControl from "../../../shared/ui/hook-form/input-digit-control/input-digit-control";

type InputFormSchema = QuotationFormSchema;
interface ActivityRateMap {
  [activityCode: string]: LabelValue[];
}
export default function QuoteActivities() {
  const { control, watch, setValue } = useFormContext<QuotationFormSchema>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "activities",
  });
  const [activities, setActivities] = useState<LabelValue[]>([]);
  const [unmappedRatePerPax, setUnmappedRatePerPax] = useState<any[]>([]);
  const [unmappedActivities, setUnmappedActivities] = useState<any[]>([]);
  const [ratePerPax, setRatePerPax] = useState<ActivityRateMap>({});
  const numDays = watch("numDays");
  const dayOptions = numDays
    ? Array.from({ length: numDays }, (_, i) => ({
        label: `Day ${i + 1}`,
        value: i + 1,
      }))
    : [];

  // THIS ARE THE LOCAL QUERIES
  useQyGetActivity("", 9999, 0, undefined, undefined, true, (response) => {
    const mappedSuggestions = (response?.data || [])
      .map((item) => ({
        label: `${item.name}`,
        value: item.code,
      }))
      .filter((x) => !!x.label);
    // Set the suggestions for the dropdown control
    setActivities(mappedSuggestions);
    setUnmappedActivities(response?.data || []);
  });

  useQyGetRatePerPax("", 9999, 0, undefined, undefined, true, (response) => {
    const mappedSuggestions = (response?.data || [])
      .map((item) => ({
        activity: item.activity,
        label: `${item.number} - ${item.size_description}`,
        value: item.code,
      }))
      .filter((x) => !!x.label);
    setUnmappedRatePerPax(response?.data || []);
    // Set the suggestions for the dropdown control
    const groupedRatesPerPax = groupByActivity(mappedSuggestions);
    setRatePerPax(groupedRatesPerPax);
  });

  const groupByActivity = (
    mappedSuggestions: Array<{ activity: string; label: string; value: string }>
  ) => {
    return mappedSuggestions.reduce((acc: ActivityRateMap, item) => {
      const activityUuid = item.activity;

      if (!acc[activityUuid]) {
        acc[activityUuid] = [];
      }

      acc[activityUuid].push({
        label: item.label,
        value: item.value,
      });

      return acc;
    }, {});
  };

  return (
    <div className="quotation-activities bg-white p-4 rounded shadow my-4">
      <DataTable value={fields} selectionMode="single" className="mb-4">
        <Column
          field="baseActivity"
          header="Activity"
          body={(item, options) => (
            <DropdownControl<InputFormSchema>
              control={control}
              name={`activities.${options.rowIndex}.baseActivity`}
              label="Activity"
              className="w-full"
              containerClassName="pb-2"
              placeholder="Select an activity"
              options={activities}
            />
          )}
        ></Column>
        <Column
          field="day"
          header="Day"
          body={(item, options) => {
            const baseActivity = watch(
              `activities.${options.rowIndex}.baseActivity`
            );
            return (
              <DropdownControl<InputFormSchema>
                control={control}
                name={`activities.${options.rowIndex}.day`}
                label="Day"
                className="w-full"
                containerClassName="pb-2"
                placeholder="Select day for activity"
                options={dayOptions}
                onSelect={(value) => {
                  const activity = unmappedActivities.find(
                    (rt) => rt.code === baseActivity
                  ) as GetActivityModel;
                  setValue(
                    `activities.${options.rowIndex}.name`,
                    activity?.name
                  );
                  setValue(
                    `activities.${options.rowIndex}.description`,
                    activity?.description
                  );
                }}
              />
            );
          }}
        ></Column>
        <Column
          field="isChildActivity"
          header="Is Child Activity"
          body={(item, options) => {
            const isChildActivity = watch(
              `activities.${options.rowIndex}.isChildActivity`
            );
            return (
              <Checkbox
                checked={isChildActivity ? true : false}
                disabled
                className="p-checkbox-sm"
              />
            );
          }}
        >
        </Column>
        <Column
          field="numPeople"
          header="Size"
          body={(item, options) => {
            const baseActivity = watch(
              `activities.${options.rowIndex}.baseActivity`
            );
            return (
              <DropdownControl<InputFormSchema>
                control={control}
                name={`activities.${options.rowIndex}.ratePerPax`}
                label="Size"
                className="w-full"
                containerClassName="pb-2"
                placeholder="Enter number of people"
                options={ratePerPax[baseActivity || ""] || []}
                onSelect={(value) => {
                  const ratePerPax = unmappedRatePerPax.find(
                    (rt) => rt.code === value
                  );
                  setValue(
                    `activities.${options.rowIndex}.childRate`,
                    ratePerPax?.child_rate || 0
                  );
                  setValue(
                    `activities.${options.rowIndex}.amount`,
                    ratePerPax?.total_amount || 0
                  );
                }}
              />
            );
          }}
        ></Column>
        <Column
          field="totalAmount"
          header="Total Amount"
          body={(item, options) => {
            return (
              <InputDigitControl<InputFormSchema>
                control={control}
                name={`activities.${options.rowIndex}.amount`}
                label="Total Amount"
                className="w-full"
                containerClassName="pb-2"
                placeholder="Set total amount"
              />
            );
          }}
        ></Column>
        <Column
          field="actions"
          header="Actions"
          body={(item, options) => (
            <Button
              severity="danger"
              type="button"
              className="mt-2"
              onClick={() => {
                remove(options.rowIndex);
              }}
              icon="pi pi-trash"
            ></Button>
          )}
        ></Column>
      </DataTable>
      <Button type="button" onClick={() => append(quoteActivityFormDefault)}>
        Add Activity
      </Button>
    </div>
  );
}
